import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, brl, formatApiError } from "@/lib/api";
import { Loader2, Download, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export function SpedDialog({ open, onOpenChange, month }) {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [agree, setAgree] = useState(false);
  useEffect(() => {
    if (!open) { return; }
    setInfo(null); setAgree(false);
    api.get("/fiscal/sped/check", { params: { month } }).then(({ data }) => setInfo(data)).catch((e) => { toast.error(formatApiError(e)); onOpenChange(false); });
  }, [open, month, onOpenChange]);
  const download = async () => {
    setBusy(true);
    try {
      const resp = await api.get("/fiscal/sped/file", { params: { month }, responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      const cd = resp.headers?.["content-disposition"] || "";
      a.href = url; a.download = (cd.match(/filename="([^"]+)"/) || [])[1] || `SPED-EFD-${month}.txt`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success("Arquivo do SPED gerado");
    } catch (e) {
      let msg = formatApiError(e);
      try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* blob */ }
      toast.error(msg);
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="sped-dialog" className="bg-zinc-950 border border-zinc-800 text-zinc-100 max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display uppercase">SPED Fiscal — {month}</DialogTitle>
          <DialogDescription className="text-zinc-400">EFD ICMS/IPI (.txt no leiaute oficial) com as NF-e/NFC-e emitidas e as compras lançadas por XML.</DialogDescription>
        </DialogHeader>
        <div data-testid="sped-pva-warning" className="rounded-md border border-amber-700/70 bg-amber-950/40 p-3 text-xs text-amber-100 flex gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-300" />
          <span>Este arquivo é uma base. O contador deve conferir e validar no PVA da Receita antes de assinar e enviar. Ajustes de apuração, inventário e créditos ficam com ele.</span>
        </div>
        {!info ? <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div> : (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <div data-testid="sped-count-invoices" className="rounded-md border border-zinc-800 p-2">Notas emitidas: <b>{info.invoices}</b></div>
              <div data-testid="sped-count-purchases" className="rounded-md border border-zinc-800 p-2">Compras por XML: <b>{info.purchases}</b></div>
              <div className="rounded-md border border-zinc-800 p-2">ICMS débitos: <b>{brl(info.icms_debitos)}</b></div>
              <div className="rounded-md border border-zinc-800 p-2">ICMS créditos: <b>{brl(info.icms_creditos)}</b></div>
            </div>
            {info.missing.length > 0 && (
              <div data-testid="sped-missing-list" className="rounded-md border border-red-800/70 bg-red-950/40 p-3 text-xs text-red-200">
                Falta preencher em <Link to="/owner/settings" className="underline">Configurações › Financeiro e fiscal</Link>: {info.missing.join(", ")}.
              </div>
            )}
            {info.warnings.length > 0 && (
              <ul data-testid="sped-warnings" className="list-disc pl-5 text-xs text-zinc-300 space-y-1">{info.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
            )}
            <label className="flex items-start gap-2 text-xs text-zinc-300">
              <input data-testid="sped-agree" type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5" />
              Entendi que o contador precisa validar no PVA antes do envio.
            </label>
            <Button data-testid="sped-download" disabled={busy || !agree || info.missing.length > 0} onClick={download} className="w-full h-11 bg-green-brand hover:bg-green-brandDark text-white font-bold uppercase">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Download className="h-4 w-4 mr-2" /> Baixar SPED (.txt)</>)}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
